import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { AdvancedThemeContext, THEME_PALETTES, createAdvancedDarkTheme, createAdvancedLightTheme } from './AdvancedThemeProvider.helpers.js';
import { useTranslation } from 'react-i18next';

/**
 * Advanced Theme Provider Component
 * 
 * Advanced theme provider with:
 * - Dark/light mode support
 * - Multiple palette options
 * - Auto theme mode (time-based)
 * - Persistent theme preferences
 * - Dynamic palette switching
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The advanced theme provider component
 */
export const AdvancedThemeProvider = ({ children }) => {
  const { _t } = useTranslation();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentPalette, setCurrentPalette] = useState('standard'); // Standard theme by default
  const [isAutoTheme, setIsAutoTheme] = useState(false);

  // Loading preferences
  useEffect(() => {
    const savedTheme = localStorage.getItem('advanced-theme-mode');
    const savedPalette = localStorage.getItem('advanced-theme-palette');
    const savedAuto = localStorage.getItem('advanced-theme-auto');
    
    if (savedTheme) setIsDarkMode(savedTheme === 'dark');
    if (savedPalette) setCurrentPalette(savedPalette);
    if (savedAuto) setIsAutoTheme(savedAuto === 'true');
  }, []);

  // Auto theme based on time
  useEffect(() => {
    if (isAutoTheme) {
      const hour = new Date().getHours();
      const shouldBeDark = hour < 7 || hour > 19;
      setIsDarkMode(shouldBeDark);
      
      // Change palette based on time
      if (hour >= 0 && hour < 6) setCurrentPalette('cyberpunk');
      else if (hour >= 6 && hour < 12) setCurrentPalette('aurora');
      else if (hour >= 12 && hour < 18) setCurrentPalette('holographic');
      else setCurrentPalette('neon');
    }
  }, [isAutoTheme]);

  // Save preferences
  useEffect(() => {
    localStorage.setItem('advanced-theme-mode', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('advanced-theme-palette', currentPalette);
    localStorage.setItem('advanced-theme-auto', isAutoTheme.toString());
  }, [isDarkMode, currentPalette, isAutoTheme]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    setIsAutoTheme(false);
  };

  const changePalette = (palette) => {
    setCurrentPalette(palette);
    setIsAutoTheme(false);
  };

  const toggleAutoTheme = () => {
    setIsAutoTheme(!isAutoTheme);
  };

  const theme = isDarkMode 
    ? createAdvancedDarkTheme(THEME_PALETTES[currentPalette])
    : createAdvancedLightTheme();

  const contextValue = {
    isDarkMode,
    currentPalette,
    isAutoTheme,
    availablePalettes: Object.keys(THEME_PALETTES),
    toggleTheme,
    changePalette,
    toggleAutoTheme,
    currentPaletteColors: THEME_PALETTES[currentPalette]
  };

  return (
    <AdvancedThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </AdvancedThemeContext.Provider>
  );
};


export default React.memo(AdvancedThemeProvider);

